import { Capacitor, registerPlugin } from '@capacitor/core'

interface NativePhotoLibraryPlugin {
  saveImage(options: { url: string; filename: string }): Promise<{ saved: boolean }>
  shareImage(options: { url: string; filename: string }): Promise<{ completed: boolean }>
}

const NativePhotoLibrary = registerPlugin<NativePhotoLibraryPlugin>('NativePhotoLibrary')

const blobToBase64 = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader()
  reader.onerror = () => reject(reader.error ?? new Error('Görsel okunamadı.'))
  reader.onload = () => resolve(String(reader.result).replace(/^data:.*?;base64,/, ''))
  reader.readAsDataURL(blob)
})

const imageBlob = async (source: string): Promise<Blob> => {
  if (source.startsWith('data:')) {
    const response = await fetch(source)
    if (!response.ok) throw new Error('Görsel verisi okunamadı.')
    return response.blob()
  }

  const response = await fetch(source, { mode: 'cors', credentials: 'omit' })
  if (!response.ok) throw new Error(`Görsel indirilemedi (${response.status}).`)
  return response.blob()
}

export const downloadImageFile = async (source: string, filename: string) => {
  if (Capacitor.isNativePlatform()) {
    if (Capacitor.getPlatform() === 'ios') {
      let saveUrl = source
      if (!source.startsWith('https://') && !source.startsWith('data:')) {
        const blob = await imageBlob(source)
        saveUrl = `data:${blob.type || 'image/png'};base64,${await blobToBase64(blob)}`
      }
      await NativePhotoLibrary.saveImage({ url: saveUrl, filename })
      return filename
    }
    const { Filesystem, Directory } = await import('@capacitor/filesystem')

    // Native URLSession/OkHttp downloads do not depend on browser CORS headers.
    if (!source.startsWith('data:') && !source.startsWith('blob:')) {
      const result = await Filesystem.downloadFile({
        url: source,
        path: `Voxnela/${filename}`,
        directory: Directory.Documents,
        recursive: true,
      })
      return result.path
    }

    const blob = await imageBlob(source)
    const result = await Filesystem.writeFile({
      path: `Voxnela/${filename}`,
      data: await blobToBase64(blob),
      directory: Directory.Documents,
      recursive: true,
    })
    return result.uri
  }

  const blob = await imageBlob(source)
  const objectUrl = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  }

  return filename
}

export const shareImageFile = async (source: string, filename: string) => {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    let shareUrl = source
    if (!source.startsWith('https://') && !source.startsWith('data:')) {
      const blob = await imageBlob(source)
      shareUrl = `data:${blob.type || 'image/png'};base64,${await blobToBase64(blob)}`
    }
    await NativePhotoLibrary.shareImage({ url: shareUrl, filename })
    return filename
  }

  return downloadImageFile(source, filename)
}
